import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, MapPin, Camera,
  Heart, User, Sparkles, Package, PartyPopper, Briefcase,
  Mountain, UtensilsCrossed, Baby, Home as HomeIcon, Bird,
} from "@/lib/icons";
import { Avatar } from "@/components/PhotoUpload";
import { selectedCategories } from "@/components/CategoryMultiSelect";

const CATEGORY_ICONS = {
  Wedding: Heart,
  Portrait: User,
  Fashion: Sparkles,
  Product: Package,
  Event: PartyPopper,
  Events: PartyPopper,
  Corporate: Briefcase,
  Landscape: Mountain,
  Food: UtensilsCrossed,
  Baby: Baby,
  Maternity: Baby,
  "Real Estate": HomeIcon,
  Wildlife: Bird,
};
export const iconFor = (name) => CATEGORY_ICONS[name] || Camera;

export default function MemberCard({ member: m, index = 0, testidPrefix = "member-card" }) {
  const CatIcon = iconFor(m.category);
  const categories = selectedCategories(m);
  const location = [m.area, m.city, m.state].filter(Boolean).join(", ") || "Location not specified";
  return (
    <Link
      to={`/member/${encodeURIComponent(m.member_id)}`}
      data-testid={`${testidPrefix}-${m.member_id}`}
      className="group glass rounded-2xl card-hover fade-up flex items-center gap-4 sm:gap-5 p-4 sm:p-5"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
    >
      {/* Round photo */}
      <div className="relative shrink-0 h-20 w-20 sm:h-24 sm:w-24 rounded-full overflow-hidden border border-white/10 bg-gradient-to-br from-violet-800/40 to-magenta-700/30">
        <Avatar
          src={m.photo_url}
          name={m.full_name}
          avatarKey={m.avatar_key}
          size="100%"
          bordered={false}
          className="transition-transform duration-500 group-hover:scale-105"
          style={{ fontSize: 32 }}
        />
      </div>

      {/* Subtle vertical divider */}
      <div className="hidden sm:block self-stretch w-px bg-white/10" />

      {/* Middle content */}
      <div className="flex-1 min-w-0">
        <div className="overline text-fuchsia-300/70 text-[10px]" data-testid={`member-id-${m.member_id}`}>{m.member_id}</div>
        <div className="mt-1 font-display text-xl sm:text-2xl leading-tight text-white line-clamp-1">
          {m.full_name}
        </div>
        {m.studio_name && (
          <div className="text-xs sm:text-sm text-white/45 line-clamp-1 mt-0.5">{m.studio_name}</div>
        )}
        <div className="mt-2 flex items-center gap-1.5 text-[11px] sm:text-xs text-white/45">
          <MapPin size={12} className="text-fuchsia-300 shrink-0" />
          <span className="truncate">{location}</span>
        </div>
      </div>

      {/* Right pills */}
      <div className="flex w-[112px] shrink-0 flex-col items-stretch gap-2 sm:w-[150px]">
        <span className="inline-flex min-h-9 items-center justify-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/[0.08] px-2.5 py-1.5 text-[10px] font-semibold text-emerald-300" title="PRONET Pro ID">
          <ShieldCheck size={13} /> Pro ID
        </span>
        {m.category ? (
          <span className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/[0.08] px-3 py-1.5 text-[11px] font-semibold text-fuchsia-200 sm:text-xs">
            <CatIcon size={12} className="shrink-0" />
            <span className="truncate">{m.category}</span>
            {categories.length > 1 && <span className="shrink-0 text-fuchsia-200/60">+{categories.length - 1}</span>}
          </span>
        ) : (
          <span className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-full border border-white/10 bg-white/[0.035] px-3 py-1.5 text-[10px] font-medium text-white/45" title="Category not added">
            <Camera size={12} className="shrink-0" />
            <span className="sm:hidden">Not added</span>
            <span className="hidden sm:inline">Category not added</span>
          </span>
        )}
      </div>
    </Link>
  );
}
